/**
 * Per-layer animation math. Pure: no DOM, no Fabric.
 *
 * Presets emit DELTAS over the authored baseline, never absolute states, so
 * in + out + emphasis compose (add offsets/angle, multiply scale/opacity)
 * without clobbering each other, and the baseline is recovered by
 * discarding the deltas.
 *
 * Convention: in/out presets take an `amount` in [0, 1] where 0 is AT REST
 * (identity delta) and 1 is FULLY APPLIED (off-screen / transparent /
 * shrunk). `in` maps time to amount = 1 - eased progress, `out` to
 * amount = eased progress. Easing may overshoot (back-out), so amounts
 * slightly outside [0, 1] are legal and produce the bounce. Emphasis presets
 * take a cycle phase in [0, 1) instead.
 */

import type {
  AnimationDirection,
  AnimationPresetId,
  LayerAnimation,
  LayerAnimationStep,
  LayerDelta,
  LayerEmphasisStep,
  LayerFrameState,
} from '@canvas/shared-types';
import { ease } from './easing';

/** What a preset needs to know about the layer and the artboard. */
export interface PresetContext {
  artboard: { width: number; height: number };
  /** On-artboard size at the authored scale. */
  size: { width: number; height: number };
  /** Where `left`/`top` sit inside the box: 0 = left/top, 0.5 = center, 1 = right/bottom. */
  origin: { x: number; y: number };
}

/** Fraction of the artboard a slide travels; far enough to clear most layers. */
const SLIDE_DISTANCE = 0.6;
/** Fraction of the artboard a pan travels; short, paired with a fade. */
const PAN_DISTANCE = 0.08;
/** Fraction of the artboard a rise/sink travels. */
const RISE_DISTANCE = 0.06;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function intensityOf(step: LayerAnimationStep): number {
  return clamp(step.intensity ?? 1, 0, 2);
}

/** Unit vector pointing from the at-rest position toward where the layer comes from / goes to. */
function directionVector(dir: AnimationDirection | undefined): { x: number; y: number } {
  switch (dir) {
    case 'right':
      return { x: 1, y: 0 };
    case 'up':
      return { x: 0, y: -1 };
    case 'down':
      return { x: 0, y: 1 };
    case 'left':
    case 'center':
    case undefined:
    default:
      return { x: -1, y: 0 };
  }
}

/**
 * Scale by `m` while keeping the layer's visual center fixed. Fabric scales
 * about the object's origin, which for the default left/top origin would
 * make pop/pulse grow from the corner.
 */
function scaleAboutCenter(m: number, ctx: PresetContext): LayerDelta {
  return {
    mulScaleX: m,
    mulScaleY: m,
    dLeft: (0.5 - ctx.origin.x) * ctx.size.width * (1 - m),
    dTop: (0.5 - ctx.origin.y) * ctx.size.height * (1 - m),
  };
}

function offset(
  dir: AnimationDirection | undefined,
  fraction: number,
  amount: number,
  ctx: PresetContext
): LayerDelta {
  const v = directionVector(dir);
  return {
    dLeft: v.x * ctx.artboard.width * fraction * amount,
    dTop: v.y * ctx.artboard.height * fraction * amount,
  };
}

/** Delta for an in/out preset at `amount` (0 = at rest, 1 = fully applied). */
export function presetDeltaAt(
  presetId: AnimationPresetId,
  step: LayerAnimationStep,
  amount: number,
  ctx: PresetContext
): LayerDelta {
  const k = intensityOf(step);
  const fade = clamp(1 - amount, 0, 1);
  switch (presetId) {
    case 'fade-in':
    case 'fade-out':
      return { mulOpacity: fade };
    case 'slide-in':
    case 'slide-out':
      return offset(step.direction, SLIDE_DISTANCE * k, amount, ctx);
    case 'pan-in':
    case 'pan-out':
      return { ...offset(step.direction, PAN_DISTANCE * k, amount, ctx), mulOpacity: fade };
    case 'rise':
      return { dTop: ctx.artboard.height * RISE_DISTANCE * k * amount, mulOpacity: fade };
    case 'sink':
      return { dTop: ctx.artboard.height * RISE_DISTANCE * k * amount, mulOpacity: fade };
    case 'pop-in':
    case 'pop-out': {
      // Floor keeps Fabric away from a zero scale, which breaks its matrix math.
      const m = Math.max(0.001, 1 - amount * clamp(k, 0, 1));
      return { ...scaleAboutCenter(m, ctx), mulOpacity: fade };
    }
    case 'none':
    case 'wipe-in':
    case 'wipe-out':
    case 'blur-in':
    case 'blur-out':
    case 'typewriter':
    case 'ken-burns':
    case 'breathe':
    case 'float':
    case 'pulse':
    case 'wiggle':
    case 'spin-slow':
    default:
      return {};
  }
}

/** Delta for an emphasis preset at cycle `phase` in [0, 1]. */
export function emphasisDeltaAt(
  step: LayerEmphasisStep,
  phase: number,
  ctx: PresetContext
): LayerDelta {
  const k = intensityOf(step);
  const wave = Math.sin(phase * Math.PI * 2);
  switch (step.preset) {
    case 'ken-burns':
      return scaleAboutCenter(1 + 0.15 * k * ease(step.easing, phase), ctx);
    case 'breathe':
      return scaleAboutCenter(1 + 0.04 * k * wave, ctx);
    case 'pulse':
      return scaleAboutCenter(1 + 0.08 * k * Math.abs(wave), ctx);
    case 'float':
      return { dTop: -ctx.artboard.height * 0.012 * k * wave };
    case 'wiggle':
      return { dAngle: 4 * k * wave };
    case 'spin-slow':
      return { dAngle: 360 * phase };
    default:
      return {};
  }
}

/** Fold deltas over the baseline: add offsets and angle, multiply scale and opacity. */
export function composeDeltas(base: LayerFrameState, deltas: LayerDelta[]): LayerFrameState {
  const out: LayerFrameState = { ...base };
  let opacityMul = 1;
  for (const d of deltas) {
    out.left += d.dLeft ?? 0;
    out.top += d.dTop ?? 0;
    out.scaleX *= d.mulScaleX ?? 1;
    out.scaleY *= d.mulScaleY ?? 1;
    out.angle += d.dAngle ?? 0;
    out.skewX += d.dSkewX ?? 0;
    out.skewY += d.dSkewY ?? 0;
    opacityMul *= d.mulOpacity ?? 1;
    if (d.visible === false) out.visible = false;
  }
  out.opacity = clamp(base.opacity * opacityMul, 0, 1);
  return out;
}

/** Progress of `t` through [start, end], clamped to [0, 1]. */
function unlerp(start: number, end: number, t: number): number {
  if (end <= start) return t >= end ? 1 : 0;
  return clamp((t - start) / (end - start), 0, 1);
}

/** Emphasis cycle phase at scene-local `t`. */
export function emphasisPhase(step: LayerEmphasisStep, t: number): number {
  const d = Math.max(1, step.durationMs);
  const loop = step.loop !== false;
  return loop ? (t % d) / d : clamp(t / d, 0, 1);
}

/** All active deltas for a layer at scene-local `t`. */
export function deltasAt(
  anim: LayerAnimation,
  t: number,
  sceneDurationMs: number,
  ctx: PresetContext
): LayerDelta[] {
  const deltas: LayerDelta[] = [];

  if (anim.emphasis && anim.emphasis.preset !== 'none') {
    deltas.push(emphasisDeltaAt(anim.emphasis, emphasisPhase(anim.emphasis, t), ctx));
  }

  if (anim.in && anim.in.preset !== 'none') {
    const start = Math.max(0, anim.in.delayMs);
    const end = start + Math.max(0, anim.in.durationMs);
    // Before its entry the layer is held fully "away"; after it, at rest.
    if (t < end) {
      const amount = 1 - ease(anim.in.easing, unlerp(start, end, t));
      deltas.push(presetDeltaAt(anim.in.preset, anim.in, amount, ctx));
    }
  }

  if (anim.out && anim.out.preset !== 'none') {
    const end = sceneDurationMs - Math.max(0, anim.out.delayMs);
    const start = end - Math.max(0, anim.out.durationMs);
    if (t > start) {
      const amount = ease(anim.out.easing, unlerp(start, end, t));
      deltas.push(presetDeltaAt(anim.out.preset, anim.out, amount, ctx));
    }
  }

  return deltas;
}

/** Resolve a layer's absolute transform at scene-local `t`. */
export function resolveFrameState(args: {
  base: LayerFrameState;
  anim: LayerAnimation | null;
  sceneLocalMs: number;
  sceneDurationMs: number;
  ctx: PresetContext;
}): LayerFrameState {
  if (!args.anim) return { ...args.base };
  return composeDeltas(
    args.base,
    deltasAt(args.anim, args.sceneLocalMs, args.sceneDurationMs, args.ctx)
  );
}

/** True when the animation has at least one non-'none' step. */
export function hasAnimation(anim: LayerAnimation | null | undefined): boolean {
  return Boolean(
    anim &&
      ((anim.in && anim.in.preset !== 'none') ||
        (anim.out && anim.out.preset !== 'none') ||
        (anim.emphasis && anim.emphasis.preset !== 'none'))
  );
}
